import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormControl, FormLabel, Grid, IconButton, Stack, Tooltip } from '@mui/material';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { debounce } from 'lodash';
import SearchField from '@/components/SearchField';
import { useGridItemProps } from '@/components/table/filterGrid';

const SEARCH_DELAY_MS = 300;

// Keeps what's typed locally and reports it after a pause, so the list doesn't re-search
// (or re-request) on every key.
const AdminSearchField = ({ search, onSearchChange }) => {
	const [text, setText] = useState(search);
	const onSearchChangeRef = useRef(onSearchChange);
	onSearchChangeRef.current = onSearchChange;

	const reportSearch = useMemo(
		() => debounce((value) => onSearchChangeRef.current(value.trim()), SEARCH_DELAY_MS),
		[],
	);
	useEffect(() => () => reportSearch.cancel(), [reportSearch]);

	// Follow the URL when it changes from outside, e.g. back / forward or clear filters
	useEffect(() => setText(search), [search]);

	return (
		<FormControl fullWidth>
			<FormLabel>Search</FormLabel>
			<SearchField
				value={text}
				onChange={(event) => {
					setText(event.target.value);
					reportSearch(event.target.value);
				}}
				onReset={() => {
					setText('');
					reportSearch.cancel();
					onSearchChange('');
				}}
				helperText=''
			/>
		</FormControl>
	);
};

/**
 * Toolbar above an admin list, laid out like the CRM table filters (useGridItemProps):
 * - search / onSearchChange: a search box, shown when onSearchChange is given. onSearchChange
 *   gets the trimmed text after a short pause in typing.
 * - filters: the area's filter controls. Each is wrapped in a filter-sized grid item, so pass
 *   one element per filter (an array, or a fragment's children).
 * - onClearFilters: shows a "Clear filters" button at the end of the filters.
 * - primaryActions: for example a Create button, on the right
 * - bulkActions: actions for selected rows, on the right; the area renders it only when
 *   rows are selected
 */
export const AdminListToolbar = ({
	search,
	onSearchChange,
	filters,
	onClearFilters,
	primaryActions,
	bulkActions,
}) => {
	const { searchGridProps, generalGridProps, clearFilterGridProps } = useGridItemProps();

	return (
		<Grid container spacing={2} width='100%' sx={{ pb: 2 }}>
			{onSearchChange && (
				<Grid {...searchGridProps}>
					<AdminSearchField search={search} onSearchChange={onSearchChange} />
				</Grid>
			)}
			{React.Children.map(filters, (filter) => (
				<Grid {...generalGridProps}>{filter}</Grid>
			))}
			{onClearFilters && (
				// Bottom of the row, level with the inputs (the item doesn't stretch to the
				// row's height on its own)
				<Grid
					{...clearFilterGridProps}
					sx={{ ...clearFilterGridProps.sx, alignSelf: 'flex-end' }}
				>
					<Tooltip title='Clear filters'>
						<IconButton
							variant='text'
							size='small'
							color='primary'
							onClick={onClearFilters}
							aria-label='Clear filters'
						>
							<FilterAltOffOutlinedIcon />
						</IconButton>
					</Tooltip>
				</Grid>
			)}
			{(bulkActions || primaryActions) && (
				<Grid size='auto' sx={{ display: 'flex', alignItems: 'flex-end' }}>
					<Stack direction='row' spacing={1} alignItems='center'>
						{bulkActions}
						{primaryActions}
					</Stack>
				</Grid>
			)}
		</Grid>
	);
};
